// @flow

import { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';

import {
  colorPalette,
  sizes,
  fontFamily,
  timings,
  fontSizes,
  colors,
} from '../../../styles/GlobalStyle';
import { useOutsideClick } from '../../shared';
import { useDocElt } from '../hooks/useDocElt';
import type { Option } from '../types';

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used in JSX below
import { Icon } from './Icon';
import { ListItem } from './ListItem';

type SelectProps = {
  id?: string,
  className?: string,
  options: Option[],
  value?: string,
  label?: string,
  onChange?: (value?: string) => any,
  isEndOfPage?: boolean,
};

const dropdownAnimation = keyframes`
  0% {
    display: none;
    opacity: 0;
    padding-top: 0;
  }
  1% {
    display: block;
  }
  100% {
    opacity: 1;
    padding-top: calc(${sizes.padding} / 2);
  }
`;

const optionAnimation = keyframes`
  0% {
    display: none;
    margin: -45px 0;
  }
  1% {
    display: block;
  }
  100% {
    margin: 5px 0;
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used in JSX below
const Container = styled.div`
  position: relative;
  padding-top: ${sizes.padding};
`;

const sharedFontStyle = css`
  font-weight: ${fontFamily.primary.black.fontWeight};
  text-transform: uppercase;
  letter-spacing: normal;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used in JSX below
const SelectHeader = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding-bottom: 11px;
  padding-top: 4px;
  cursor: pointer;
  outline: none;

  ${sharedFontStyle}

  svg {
    transition: transform ${timings.hover};

    ${({ $opened }) =>
      $opened
      && css`
        transform: rotate(180deg);
      `}
  }

  &:before {
    content: '';
    position: absolute;
    bottom: 0;
    display: block;
    width: 100%;
    height: 2px;
    border-radius: ${sizes.borderRadius};
    background-color: ${colorPalette.primary.active};
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used in JSX below
const SelectDropdown = styled.div`
  top: 100%;
  display: none;
  opacity: 0;
  width: 100%;
  background-color: ${colorPalette.dark.active};
  z-index: 3;
  animation-name: ${dropdownAnimation};
  animation-duration: ${timings.hover};

  overflow: auto;
  border-radius: ${sizes.borderRadius};

  ${({ $isEndOfPage, $maxHeight, $opened }) => css`
    position: ${$isEndOfPage ? 'relative' : 'absolute'};
    max-height: min(${$maxHeight}, 350px);
    ${$opened
    && css`
      display: block;
      opacity: 1;
      padding-top: calc(${sizes.padding} / 2);
    `};
  `}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used in JSX below
const SelectOption = styled(ListItem)`
  margin: -45px 0;
  animation-name: ${optionAnimation};
  animation-duration: ${timings.hover};

  ${({ $opened }) =>
    $opened
    && css`
      margin: 5px 0;
    `}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used in JSX below
const TopLabel = styled.span`
  font-weight: ${fontFamily.primary.black.fontWeight};
  text-transform: uppercase;
  font-size: ${fontSizes.small.size};
  color: ${colors.yellow};
`;

export const Select = ({
  id,
  className,
  options,
  value,
  label,
  onChange,
  isEndOfPage,
}: SelectProps) => {
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);
  const [opened, setOpened] = useState(false);
  const ref = useRef();
  const headerRef = useRef();
  const dropdownRef = useRef();
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState('inherit');

  const { getElementCoords, getFullHeight } = useDocElt();

  useOutsideClick(ref, () => {
    setOpened(false);
  });

  useEffect(() => {
    if (onChange && selectedOption) {
      onChange(selectedOption.value);
    }
  }, [onChange, selectedOption]);

  useEffect(() => {
    if (selectedOption) {
      return;
    }

    const selected = value
      ? options.find(item => item.value === value) || options[0] || null
      : options[0] || null;

    setSelectedOption(selected);

    if (onChange && !selected) {
      onChange(undefined);
    }
  }, [options, value, selectedOption, onChange]);

  const toggleOpened = useCallback(() => {
    setOpened(!opened);
  }, [opened]);

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        toggleOpened();
      }
    },
    [toggleOpened],
  );

  const selectOption = useCallback(
    (selectedValue) => {
      const option = options.find(item => item.value === selectedValue);
      if (option) {
        setSelectedOption(option);
        setOpened(false);
      }
    },
    [options],
  );

  useLayoutEffect(() => {
    const handleResize = () => {
      if (!headerRef.current) {
        return;
      }

      const headerRect = getElementCoords(headerRef.current);
      const pageHeight = getFullHeight();

      setDropdownMaxHeight(`calc(${pageHeight - headerRect.bottom}px - ${sizes.padding})`);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [getElementCoords, getFullHeight]);

  return (
    <>
      <Container
        ref={ref}
        className={className}
      >
        <TopLabel>{label}</TopLabel>
        <SelectHeader
          id={id}
          ref={headerRef}
          $opened={opened}
          role={'button'}
          tabIndex={0}
          onClick={toggleOpened}
          onKeyDown={onKeyDown}
        >
          {selectedOption && selectedOption.label}
          <Icon name={'icon_arrow_down'} />
        </SelectHeader>
        <SelectDropdown
          ref={dropdownRef}
          $opened={opened}
          $isEndOfPage={isEndOfPage}
          $maxHeight={dropdownMaxHeight}
        >
          <ul>
            {options.map(option => (
              <li key={option.value}>
                <SelectOption
                  id={id ? `${id}_${option.value}` : undefined}
                  $opened={opened}
                  value={option.value}
                  active={selectedOption ? selectedOption.value === option.value : false}
                  onClick={(event: string) => {
                    selectOption(event);
                  }}
                >
                  {option.label}
                </SelectOption>
              </li>
            ))}
          </ul>
        </SelectDropdown>
      </Container>
    </>
  );
};
